import { createApi } from '@reduxjs/toolkit/query/react'
import { customFetchBaseQuery } from '../app/baseQuery'
import { toGoogleDateTime } from '../../util/date'
import type {
  PatientExistsRequest,
  PatientExistsResponse,
  ValidatePromoCodeRequest,
  ValidatePromoCodeResponse,
} from '../../types/patient-api-gateway/api/patient/v1/patient_gateway_service'
import type { DateTime as DateTimeLuxon } from 'luxon'
import Personnummer from 'personnummer'

export type ValidatePromoCodeQueryArgs = {
  promoCode: string
  clinicId: string
  treatmentId: string
  originalPrice?: number
  appointmentDateTime?: DateTimeLuxon
  ssn?: string
}

export type PatientExistsQueryArgs = {
  appointmentId: string
  clinicId: string
  ssn: string
}

export const promoCodeApi = createApi({
  reducerPath: 'promoCodeApi',
  baseQuery: customFetchBaseQuery,
  tagTypes: ['PromoCode'],
  endpoints: (builder) => ({
    validatePromoCode: builder.mutation<
      ValidatePromoCodeResponse,
      ValidatePromoCodeQueryArgs
    >({
      query: ({
        clinicId,
        promoCode,
        treatmentId,
        appointmentDateTime,
        originalPrice,
        ssn,
      }) => ({
        url: '/api.patient.v1.PatientGatewayService/ValidatePromoCode',
        method: 'POST',
        body: {
          clinicId,
          promoCode,
          treatmentId,
          appointmentDatetime: toGoogleDateTime(appointmentDateTime),
          originalPrice: {
            currencyCode: 'SEK',
            units: String(originalPrice),
            nanos: 0,
          },
          swedishPersonalIdentityNumber: {
            twelveDigitNumber: ssn,
          },
        } satisfies ValidatePromoCodeRequest,
      }),
      extraOptions: {
        forceRefetch: true,
        maxRetries: 3,
      },
    }),
    getPatientExists: builder.mutation<
      PatientExistsResponse,
      PatientExistsQueryArgs
    >({
      query: ({ appointmentId, clinicId, ssn }) => ({
        url: '/api.patient.v1.PatientGatewayService/PatientExists',
        method: 'POST',
        body: {
          appointmentClaim: {
            appointmentId,
            clinicId,
          },
          swedishPersonalIdentityNumber: {
            twelveDigitNumber: Personnummer.parse(ssn).format(true),
          },
        } satisfies PatientExistsRequest,
      }),
      extraOptions: {
        forceRefetch: true,
        maxRetries: 3,
      },
    }),
  }),
})

export const { useValidatePromoCodeMutation, useGetPatientExistsMutation } =
  promoCodeApi
